import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  Search,
  Loader2,
  TrendingUp,
  TrendingDown,
  Brain,
  TrendingUpIcon,
  ArrowLeft,
  Clock,
} from "lucide-react";
import { DesktopLayout } from "@/layouts";
import { ensureUsdtSymbol, removeUsdtSuffix } from "@/utils/symbols";
import {
  usePreviousAlerts,
  useLeaderboardAlerts,
  useIncomingListings,
} from "@/hooks/useCryptoAlerts";
import { useLeaderboard } from "@/hooks/useEventLeaderboard";
import { useTokenMetadata } from "@/hooks/useTokenMetadata";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { LeaderboardTokenTile } from "@/components/LeaderboardTokenTile";
import { IncomingListingTile } from "@/components/IncomingListingTile";
import { TokenIcon } from "@/components/TokenIcon";
import { AlertHistory } from "@/components/AlertHistory";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { PositionTradeDrawerDesktop } from "@/pages/alerts/drawers/PositionTradeDrawer.Desktop";

const LEADERBOARD_DISPLAY_COUNT = 10;

/**
 * Desktop version of the Analyze page
 * - Dashboard: Leaderboards (top gainers/losers) + Incoming listings + Search
 * - Analysis: Full token analysis with back button
 */
export const AnalyzePageDesktop: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const symbolFromUrl = searchParams.get("symbol");
  const [inputSymbol, setInputSymbol] = useState("");
  const [analyzedSymbol, setAnalyzedSymbol] = useState<string | null>(
    symbolFromUrl
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);

  // Sync URL with analyzed symbol
  useEffect(() => {
    if (symbolFromUrl !== analyzedSymbol) {
      setAnalyzedSymbol(symbolFromUrl);
    }
  }, [symbolFromUrl]);

  const handleAnalyzeSymbol = useCallback(
    (symbol: string) => {
      const normalized = ensureUsdtSymbol(symbol.trim().toUpperCase());
      setAnalyzedSymbol(normalized);
      setSearchParams({ symbol: removeUsdtSuffix(normalized) });
    },
    [setSearchParams]
  );

  const handleBackToDashboard = useCallback(() => {
    setAnalyzedSymbol(null);
    setInputSymbol("");
    setSearchParams({});
  }, [setSearchParams]);

  // Normalize symbol for API calls
  const normalizedSymbol = useMemo(
    () => (analyzedSymbol ? ensureUsdtSymbol(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const baseSymbol = useMemo(
    () => (analyzedSymbol ? removeUsdtSuffix(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const isAnalysisView = !!analyzedSymbol;

  // Dashboard data
  const { data: leaderboardData, isLoading: isLoadingLeaderboard } =
    useLeaderboardAlerts(!isAnalysisView);
  const { data: incomingListings = [], isLoading: isLoadingIncoming } =
    useIncomingListings(!isAnalysisView);

  const {
    sortedGainers,
    sortedLosers,
    pricedGainers,
    pricedLosers,
    isPriceLoading,
    handlePriceUpdate,
  } = useLeaderboard(leaderboardData, isLoadingLeaderboard);

  const visibleGainersIds = useMemo(
    () =>
      new Set(
        pricedGainers.slice(0, LEADERBOARD_DISPLAY_COUNT).map((a) => a.id)
      ),
    [pricedGainers]
  );

  const visibleLosersIds = useMemo(
    () =>
      new Set(
        pricedLosers.slice(0, LEADERBOARD_DISPLAY_COUNT).map((a) => a.id)
      ),
    [pricedLosers]
  );

  // Analysis view data
  const {
    data: tokenMetadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
  } = useTokenMetadata(baseSymbol, isAnalysisView);

  const { data: previousAlerts = [], isLoading: isLoadingAlerts } =
    usePreviousAlerts(userId, baseSymbol, "", isAnalysisView);

  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);

  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, isAnalysisView);

  const globallyAvailableExchangeCodes = useMemo(() => {
    const allExchanges = tokenAvailability?.userExchanges ?? [];
    return allExchanges.filter(
      (code) => !code.endsWith("-demo") && !code.endsWith("-testnet")
    );
  }, [tokenAvailability?.userExchanges]);

  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );

  const supportedUserExchangeCodes = useMemo(() => {
    return exchanges
      .filter((e) => !e.code.endsWith("-demo") && !e.code.endsWith("-testnet"))
      .map((e) => e.code)
      .filter((code) => globallyAvailableSet.has(code));
  }, [exchanges, globallyAvailableSet]);

  const hasTradableExchange = supportedUserExchangeCodes.length > 0;

  const handleOpenTrade = useCallback(() => setIsTradeDrawerOpen(true), []);

  const handleAnalyze = useCallback(() => {
    if (!inputSymbol.trim()) return;
    setIsAnalyzing(true);
    handleAnalyzeSymbol(inputSymbol);
    setTimeout(() => setIsAnalyzing(false), 500);
  }, [inputSymbol, handleAnalyzeSymbol]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleAnalyze();
    },
    [handleAnalyze]
  );

  return (
    <DesktopLayout
      pageTitle={isAnalysisView ? `Analyze ${baseSymbol}` : "Analyze Token"}
      pageDescription="Deep dive into any cryptocurrency token with comprehensive analysis"
      showRightSidebar={false}
    >
      {/* Search */}
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex gap-3 items-center">
          <div className="flex-1 relative">
            <input
              type="text"
              value={isAnalysisView ? baseSymbol : inputSymbol}
              onChange={(e) =>
                isAnalysisView ? null : setInputSymbol(e.target.value)
              }
              onKeyDown={handleKeyDown}
              placeholder="Enter token symbol (e.g., BTC, ETH, SOL)"
              className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
              readOnly={isAnalysisView}
            />
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary pointer-events-none" />
          </div>
          {!isAnalysisView && (
            <button
              onClick={handleAnalyze}
              disabled={!inputSymbol.trim() || isAnalyzing}
              className="px-6 py-3 bg-accent hover:bg-accent/80 disabled:bg-accent/50 text-white font-medium rounded-xl transition-all duration-150 shadow-lg hover:shadow-accent/20 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Brain className="w-5 h-5" />
                  <span>Analyze</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Dashboard View */}
      {!isAnalysisView && (
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Leaderboards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Top Gainers */}
            <div className="bg-secondary border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-text-primary flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Top Gainers
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Recent listings with highest price increase
              </p>
              {isLoadingLeaderboard ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-accent" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Tiles always rendered so WS subscriptions are active.
                      isPriceLoading tiles are hidden via CSS, not unmounted. */}
                  {isPriceLoading && (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-accent" />
                    </div>
                  )}
                  {!isPriceLoading && visibleGainersIds.size === 0 && (
                    <p className="text-sm text-text-secondary py-4">
                      No listings with price data
                    </p>
                  )}
                  {sortedGainers.map((alert) => (
                    <div
                      key={`gainers-${alert.id}`}
                      className={
                        isPriceLoading || !visibleGainersIds.has(alert.id)
                          ? "hidden"
                          : ""
                      }
                    >
                      <LeaderboardTokenTile
                        alert={alert}
                        onPriceUpdate={handlePriceUpdate}
                        onAnalyze={handleAnalyzeSymbol}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Losers */}
            <div className="bg-secondary border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-text-primary flex items-center gap-2 mb-4">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Top Losers
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Recent listings with largest price decrease
              </p>
              {isLoadingLeaderboard ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-accent" />
                </div>
              ) : (
                <div className="space-y-3">
                  {isPriceLoading && (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-accent" />
                    </div>
                  )}
                  {!isPriceLoading && visibleLosersIds.size === 0 && (
                    <p className="text-sm text-text-secondary py-4">
                      No listings with price data
                    </p>
                  )}
                  {sortedLosers.map((alert) => (
                    <div
                      key={`losers-${alert.id}`}
                      className={
                        isPriceLoading || !visibleLosersIds.has(alert.id)
                          ? "hidden"
                          : ""
                      }
                    >
                      <LeaderboardTokenTile
                        alert={alert}
                        onPriceUpdate={handlePriceUpdate}
                        onAnalyze={handleAnalyzeSymbol}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Incoming Listings */}
          <div className="bg-secondary border border-border rounded-xl p-6">
            <h3 className="text-lg font-bold text-text-primary flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-amber-400" />
              Incoming Listings
            </h3>
            <p className="text-sm text-text-secondary mb-4">
              Tokens listed on pre-market, roadmap, or coming soon
            </p>
            {isLoadingIncoming ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : incomingListings.length === 0 ? (
              <p className="text-sm text-text-secondary py-4">
                No incoming listings at the moment
              </p>
            ) : (
              <div className="space-y-3">
                {incomingListings.map((alert) => (
                  <IncomingListingTile
                    key={alert.id}
                    alert={alert}
                    onAnalyze={handleAnalyzeSymbol}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {isAnalysisView && (
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Token Header */}
          <div className="bg-secondary border border-border rounded-xl p-6">
            <div className="flex items-center gap-4">
              <TokenIcon
                symbol={baseSymbol}
                logo={tokenMetadata?.logo}
                size="lg"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-text-primary">
                    {baseSymbol}
                  </h2>
                  {tokenMetadata?.name && (
                    <span className="text-lg text-text-secondary">
                      {tokenMetadata.name}
                    </span>
                  )}
                </div>
                {(isLoadingMetadata ||
                  isLoadingAlerts ||
                  isLoadingAvailability ||
                  isLoadingExchanges) && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-accent" />
                    <span className="text-sm text-text-secondary">
                      Loading token data...
                    </span>
                  </div>
                )}
                {metadataError && !isLoadingMetadata && (
                  <p className="text-sm text-yellow-500">
                    ⚠️ Could not load complete token metadata
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Token Metadata Section */}
          {tokenMetadata?.description || tokenMetadata?.urls ? (
            <div className="bg-secondary border border-border rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-bold text-text-primary flex items-center gap-2">
                <Brain className="w-5 h-5 text-accent" />
                Token Information
              </h3>
              {tokenMetadata.description && (
                <div>
                  <p className="text-xs text-text-secondary mb-2 uppercase tracking-wide">
                    Description
                  </p>
                  <p className="text-sm text-text-primary leading-relaxed">
                    {tokenMetadata.description}
                  </p>
                </div>
              )}
              {tokenMetadata.urls && (
                <div>
                  <p className="text-xs text-text-secondary mb-3 uppercase tracking-wide">
                    Official Links
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tokenMetadata.urls.website?.[0] && (
                      <a
                        href={tokenMetadata.urls.website[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg bg-primary/60 text-sm text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Website
                      </a>
                    )}
                    {tokenMetadata.urls.twitter?.[0] && (
                      <a
                        href={tokenMetadata.urls.twitter[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg bg-primary/60 text-sm text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Twitter
                      </a>
                    )}
                    {tokenMetadata.urls.source_code?.[0] && (
                      <a
                        href={tokenMetadata.urls.source_code[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg bg-primary/60 text-sm text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        GitHub
                      </a>
                    )}
                    {tokenMetadata.urls.technical_doc?.[0] && (
                      <a
                        href={tokenMetadata.urls.technical_doc[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg bg-primary/60 text-sm text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Whitepaper
                      </a>
                    )}
                    {tokenMetadata.urls.explorer?.[0] && (
                      <a
                        href={tokenMetadata.urls.explorer[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg bg-primary/60 text-sm text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Explorer
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Alert History Section */}
          <AlertHistory
            alerts={previousAlerts}
            variant="page-desktop"
            baseSymbol={baseSymbol}
          />

          {isLoadingAlerts && (
            <div className="bg-secondary border border-border rounded-xl p-6">
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
                <span className="text-sm text-text-secondary">
                  Loading alert history...
                </span>
              </div>
            </div>
          )}

          {/* Exchange Availability */}
          {globallyAvailableExchangeCodes.length > 0 && (
            <div className="bg-secondary border border-border rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-text-primary">
                  Exchange Availability
                </h3>
                {hasTradableExchange && (
                  <button
                    onClick={handleOpenTrade}
                    className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white font-medium rounded-lg transition-all duration-150 shadow-lg hover:shadow-accent/20"
                  >
                    <TrendingUpIcon className="w-4 h-4" />
                    <span>Trade</span>
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {globallyAvailableExchangeCodes.map((exchangeCode) => {
                  const isUserConnected =
                    supportedUserExchangeCodes.includes(exchangeCode);
                  return (
                    <span
                      key={exchangeCode}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                        isUserConnected
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-green-500/10 text-green-400 border border-green-500/20"
                      }`}
                    >
                      {exchangeCode}
                      {isUserConnected && " ✓"}
                    </span>
                  );
                })}
              </div>
              {hasTradableExchange && (
                <p className="text-xs text-green-400 mt-2">
                  ✓ Available on your connected exchanges
                </p>
              )}
            </div>
          )}

          {/* Price Chart */}
          <div className="bg-secondary border border-border rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-text-primary">Price Chart</h3>
            <TradingViewChartWidget
              key={`chart-${normalizedSymbol}`}
              symbol={normalizedSymbol}
            />
          </div>

          {/* AI Analysis */}
          <div className="bg-secondary border border-border rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-text-primary flex items-center gap-2">
              <Brain className="w-5 h-5 text-accent" />
              AI-Powered Analysis
            </h3>
            <TokenAnalysisSection tokenSymbol={baseSymbol} />
          </div>
        </div>
      )}

      {/* Trade Drawer */}
      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={normalizedSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => setIsTradeDrawerOpen(open)}
        onGoToPosition={() => {}}
      />

      {/* Floating Back FAB - only when viewing token analysis */}
      {isAnalysisView && (
        <button
          onClick={handleBackToDashboard}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-accent hover:bg-accent/90 text-white shadow-lg hover:shadow-xl transition-all duration-200"
          aria-label="Back"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      )}
    </DesktopLayout>
  );
};
